import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const SEV = { high: "bg-red-600 text-white", medium: "bg-yellow-600 text-black", low: "bg-neutral-700 text-white" };

function Column({ label, a }) {
  const result = a?.result || {};
  return (
    <div className="border border-neutral-800 rounded-md bg-neutral-900 p-5">
      <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">{label}</div>
      <div className="font-display-heavy text-2xl mt-1">{a.sport}</div>
      <div className="text-neutral-500 text-sm">{new Date(a.created_at).toLocaleDateString()}</div>
      {result.summary && <p className="text-sm text-neutral-200 mt-3">{result.summary}</p>}

      {result.strengths?.length > 0 && (
        <div className="mt-4">
          <div className="uppercase text-xs text-green-500 font-display tracking-widest mb-2">Strengths</div>
          <ul className="list-disc pl-5 text-sm text-neutral-300 space-y-1">
            {result.strengths.map((s) => <li key={`str-${s.slice(0, 20)}`}>{s}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-4">
        <div className="uppercase text-xs text-red-500 font-display tracking-widest mb-2">Fix list ({(result.issues || []).length})</div>
        <div className="space-y-3">
          {(result.issues || []).map((it) => (
            <div key={it.id || it.title} className="bg-neutral-950 rounded-md p-3 border border-neutral-800">
              <div className="flex justify-between items-start gap-2">
                <div className="font-semibold text-sm">{it.title}</div>
                <span className={`text-[10px] uppercase font-display font-bold tracking-widest px-1.5 py-0.5 rounded ${SEV[it.severity] || SEV.medium}`}>{it.severity}</span>
              </div>
              <p className="text-xs text-neutral-400 mt-1">{it.explanation}</p>
              {it.drill && <p className="text-xs text-red-400 mt-1"><span className="text-neutral-500">Drill:</span> {it.drill.name}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Compare() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const aId = sp.get("a");
  const [bId, setBId] = useState(sp.get("b") || "");
  const [options, setOptions] = useState([]);
  const [pair, setPair] = useState(null);

  useEffect(() => {
    api.get("/analyses").then((r) => {
      const done = (r.data.analyses || []).filter((a) => a.status === "complete");
      setOptions(done);
    });
  }, []);

  useEffect(() => {
    if (!aId || !bId) return;
    api.get(`/analyses-compare?a=${aId}&b=${bId}`).then((r) => setPair(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load"));
  }, [aId, bId]);

  const aMeta = options.find((o) => o.id === aId);
  const bOptions = options.filter((o) => o.id !== aId && (!aMeta || o.sport === aMeta.sport));

  return (
    <div>
      <Link to="/dashboard" className="text-neutral-400 hover:text-white text-sm font-display uppercase tracking-widest">← Back</Link>
      <div className="rs-underline mt-4 mb-3" />
      <h1 className="font-display-heavy text-4xl">COMPARE · BEFORE / AFTER</h1>
      <p className="text-neutral-400 mt-2">Line two analyses up side by side to see your progress.</p>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-4">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest mb-2">Before</div>
          {aMeta ? (
            <div className="font-display-heavy">{aMeta.sport} · {new Date(aMeta.created_at).toLocaleDateString()}</div>
          ) : <div className="text-neutral-500">Pick from dashboard first.</div>}
        </div>
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-4">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest mb-2">After</div>
          <Select value={bId} onValueChange={(v) => { setBId(v); nav(`/compare?a=${aId}&b=${v}`, { replace: true }); }}>
            <SelectTrigger data-testid="compare-b-select" className="h-11 bg-neutral-950 border-neutral-700"><SelectValue placeholder="Pick a second analysis" /></SelectTrigger>
            <SelectContent>
              {bOptions.length === 0 && <div className="p-3 text-neutral-400 text-sm">No other completed analyses in this sport yet.</div>}
              {bOptions.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.sport} · {new Date(o.created_at).toLocaleDateString()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {pair && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <Column label="Before" a={pair.a} />
          <Column label="After" a={pair.b} />
        </div>
      )}
    </div>
  );
}
